import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { usePermisos } from '../../hooks/usePermisos';
import {
  Search, Landmark, Building2, AlertTriangle, CheckCircle2, XCircle,
  ShieldAlert, FileWarning, Loader2, User2, LineChart, TrendingUp,
  Gauge, ChevronDown, Calculator, Save, Info, FileDown,
} from 'lucide-react';

const fmtMoney = (n) =>
  (n || n === 0)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';

const sitColor = (s) => {
  if (!s || s <= 1) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (s === 2) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-rose-600 bg-rose-50 border-rose-200';
};

const sitBar = (s) => {
  if (!s || s <= 1) return 'bg-emerald-500';
  if (s === 2) return 'bg-amber-500';
  return 'bg-rose-500';
};

const tierColor = (tier) => {
  if (tier === 'Alto') return 'text-emerald-600';
  if (tier === 'Medio') return 'text-amber-600';
  if (tier === 'Bajo') return 'text-rose-600';
  return 'text-slate-400';
};

const initials = (s) => {
  const p = String(s || '').trim().split(' ').filter(Boolean);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '??';
};

const Field = ({ label, value, testid }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-medium text-slate-700 dark:text-slate-200 break-words" data-testid={testid}>{value ?? '—'}</p>
  </div>
);

const SourceHeader = ({ icon: Icon, title, ok, cached }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-slate-500" />
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
    </div>
    <div className="flex items-center gap-2">
      {cached && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-600" title="Dato reusado del caché (no consumió cupo)">caché</span>
      )}
      <span className={`h-2.5 w-2.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-slate-300'}`} title={ok ? 'OK' : 'Sin datos'} />
    </div>
  </div>
);

const fmtMeses = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseInt(v, 10);
  return isNaN(n) ? String(v) : `${n} meses`;
};
const fmtPeriodo = (p) => {
  if (!p) return '—';
  const s = String(p);
  return s.length === 6 ? `${s.slice(4, 6)}/${s.slice(0, 4)}` : s;
};

const KV = ({ k, v }) => (
  <>
    <span className="text-slate-500">{k}</span>
    <span className="text-right font-medium text-slate-700 dark:text-slate-200 break-words">
      {(v ?? '') === '' ? '—' : v}
    </span>
  </>
);

const NosisGroup = ({ label, children }) => (
  <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{label}</p>
    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">{children}</div>
  </div>
);

// Vista enriquecida de Nosis (todas las variables del grupo VR1, agrupadas como el motor original)
const NosisDetail = ({ nosis, scoreTier, ingresoEstimado }) => {
  const [showAll, setShowAll] = useState(false);
  const endeud = ingresoEstimado > 0 && nosis.compromiso_mensual
    ? `${((nosis.compromiso_mensual / ingresoEstimado) * 100).toFixed(1).replace('.', ',')}%`
    : '—';
  const vars = nosis._variables || {};
  const varKeys = Object.keys(vars).sort();
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-3" data-testid="motor-nosis-score">
        <span className={`text-3xl font-bold ${tierColor(scoreTier)}`}>{nosis.score ?? '—'}</span>
        <span className="text-xs text-slate-500">
          Score · NSE {nosis.nse || '—'}{nosis.nse_percentil ? ` (p${nosis.nse_percentil})` : ''}
        </span>
      </div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mt-1">
        {nosis.razon_social || `${nosis.apellido || ''} ${nosis.nombre || ''}`.trim() || '—'}
      </p>
      <p className="text-xs text-slate-500">CUIT {nosis.cuit || '—'} · DNI {nosis.dni || '—'}</p>

      <NosisGroup label="Identidad">
        <KV k="Fallecido" v={nosis.fallecido} />
        <KV k="Sexo" v={nosis.sexo} />
        <KV k="Edad" v={nosis.edad} />
        <KV k="Fec. nacimiento" v={nosis.fecha_nacimiento} />
      </NosisGroup>

      <NosisGroup label="Domicilio AFIP">
        <KV k="Calle" v={`${nosis.dom_calle || ''} ${nosis.dom_nro || ''}`.trim() || '—'} />
        <KV k="Piso / Dto" v={(nosis.dom_piso || nosis.dom_dto) ? `${nosis.dom_piso || '-'} / ${nosis.dom_dto || '-'}` : '—'} />
        <KV k="Localidad" v={nosis.dom_loc} />
        <KV k="Provincia" v={nosis.dom_prov} />
        <KV k="CP" v={nosis.dom_cp} />
      </NosisGroup>

      <NosisGroup label="Empleo">
        <KV k="Es empleado" v={nosis.empleado} />
        <KV k="Última fecha empleado" v={fmtPeriodo(nosis.empleado_ult_fecha)} />
        <KV k="Es empleador" v={nosis.empleador_es} />
        <KV k="Es jubilado" v={nosis.jubilado} />
        <KV k="Es pensionado" v={nosis.pensionado_es} />
        <KV k="Empleado doméstico" v={nosis.emp_domestico_es} />
        <KV k="Antigüedad laboral" v={fmtMeses(nosis.antiguedad_laboral)} />
        <KV k="Antig. empleador actual" v={fmtMeses(nosis.antiguedad_laboral_actual)} />
        <KV k="Antigüedad AFIP" v={fmtMeses(nosis.antiguedad_afip)} />
        <KV k="Inscripción AFIP" v={nosis.fecha_inicial_afip} />
      </NosisGroup>

      <NosisGroup label="Situación fiscal">
        <KV k="Monotributista" v={nosis.monotributo ? `${nosis.monotributo}${nosis.monotributo_cat ? ` (cat. ${nosis.monotributo_cat})` : ''}` : '—'} />
        <KV k="Antig. monotributo" v={fmtMeses(nosis.monotributo_antig)} />
        <KV k="Autónomo" v={nosis.autonomo ? `${nosis.autonomo}${nosis.autonomo_cat ? ` (cat. ${nosis.autonomo_cat})` : ''}` : '—'} />
        <KV k="Antig. autónomo" v={fmtMeses(nosis.autonomo_antig)} />
        <KV k="IVA" v={nosis.iva_condicion} />
        <KV k="Ganancias" v={nosis.ganancias_condicion} />
        <KV k="CNE" v={nosis.cne_tiene ? `${nosis.cne_tiene}${nosis.cne_estado ? ` (${nosis.cne_estado})` : ''}` : '—'} />
      </NosisGroup>

      <NosisGroup label="Actividad principal">
        <KV k="Código" v={nosis.act_cod} />
        <KV k="Descripción" v={nosis.act_desc} />
        <KV k="Sector" v={nosis.act_sector} />
        <KV k="Inicio actividad" v={nosis.act_fec_inicio} />
      </NosisGroup>

      <NosisGroup label="Endeudamiento y cumplimiento">
        <KV k="Compromiso mensual" v={fmtMoney(nosis.compromiso_mensual)} />
        <KV k="Endeudamiento" v={endeud} />
        <KV k="Cumplimiento 12m" v={nosis.cumplimiento} />
        <KV k="Períodos c/ mora 3m" v={nosis.moras_3m} />
        <KV k="Períodos c/ mora 12m" v={nosis.moras_12m} />
      </NosisGroup>

      <NosisGroup label="Judicial">
        <KV k="Juicios 12m" v={nosis.juicios_12m} />
        <KV k="Concursos / quiebras 12m" v={nosis.concursos} />
        <KV k="Pedidos de quiebra 12m" v={nosis.pedidos_quiebra_12m} />
      </NosisGroup>

      <NosisGroup label="Referencias comerciales">
        <KV k="Tiene refs. 6m" v={nosis.rc_6m_tiene} />
        <KV k="Cant. refs. 6m" v={nosis.rc_6m_cant} />
        <KV k="Refs. vigentes" v={nosis.rc_vig_cant} />
        <KV k="Fuente vigente" v={nosis.rc_vig_fuente} />
      </NosisGroup>

      <NosisGroup label="Consultas (credit shopping)">
        <KV k="Financieras 1m/3m/12m" v={`${nosis.co_1m_finan ?? 0} / ${nosis.co_3m_finan ?? 0} / ${nosis.co_12m_finan ?? 0}`} />
        <KV k="Bancarias 1m/3m/12m" v={`${nosis.co_1m_banca ?? 0} / ${nosis.co_3m_banca ?? 0} / ${nosis.co_12m_banca ?? 0}`} />
        <KV k="Otras 1m/3m/12m" v={`${nosis.co_1m_otros ?? 0} / ${nosis.co_3m_otros ?? 0} / ${nosis.co_12m_otros ?? 0}`} />
        <KV k="Total 12m" v={nosis.co_12m_total} />
      </NosisGroup>

      {varKeys.length > 0 && (
        <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            data-testid="motor-nosis-toggle-vars"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? 'rotate-180' : ''}`} />
            {showAll ? 'Ocultar' : 'Ver'} todas las variables ({varKeys.length})
          </button>
          {showAll && (
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs mt-2 max-h-72 overflow-auto" data-testid="motor-nosis-all-vars">
              {varKeys.map((k) => (
                <React.Fragment key={k}>
                  <span className="text-slate-400 font-mono">{k}</span>
                  <span className="text-right text-slate-600 dark:text-slate-300 break-words">{String(vars[k] === '' ? '—' : vars[k])}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Calculadora de capacidad (interactiva, cliente) ──────────────────────────
const Calculadora = ({ ingresoBase, config, admin }) => {
  const [rci, setRci] = useState(config.rci);
  const [tna, setTna] = useState(config.tna);
  const [plazo, setPlazo] = useState(config.plazo);
  const [res, setRes] = useState(null);
  const [saving, setSaving] = useState(false);

  const calcular = () => {
    const rciF = parseFloat(rci) / 100;
    const tnaF = parseFloat(tna) / 100;
    const p = parseInt(plazo, 10);
    const base = Number(ingresoBase) || 0;
    if (!rciF || !p || !base) { setRes(null); return; }
    const cuota = base * rciF;
    const i = tnaF / 12;
    const monto = i > 0 ? (cuota * (1 - Math.pow(1 + i, -p)) / i) : cuota * p;
    setRes({ base, cuota, monto });
  };

  // Recalcula al montar / cuando cambia el ingreso base.
  useEffect(() => { calcular(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ingresoBase]);

  const guardarDefault = async () => {
    setSaving(true);
    try {
      await api.put('/motor/calc-config', { rci: parseFloat(rci), tna: parseFloat(tna), plazo: parseInt(plazo, 10) });
      toast.success('Parámetros guardados como predeterminados (globales)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar los parámetros');
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-calculadora">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-teal-600" />
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Calculadora de capacidad</h3>
        </div>
        {admin && (
          <Button variant="outline" size="sm" onClick={guardarDefault} disabled={saving} data-testid="motor-calc-guardar-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Guardar como predeterminado
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500 block mb-1">RCI (%)</label>
          <Input type="number" min="1" max="100" step="1" value={rci}
            onChange={(e) => setRci(e.target.value)} className="h-9" data-testid="motor-calc-rci" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">TNA (%)</label>
          <Input type="number" min="0" step="0.01" value={tna}
            onChange={(e) => setTna(e.target.value)} className="h-9" data-testid="motor-calc-tna" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Plazo (meses)</label>
          <Input type="number" min="1" max="120" step="1" value={plazo}
            onChange={(e) => setPlazo(e.target.value)} className="h-9" data-testid="motor-calc-plazo" />
        </div>
        <Button onClick={calcular} className="bg-teal-600 hover:bg-teal-700 text-white h-9" data-testid="motor-calc-calcular-btn">
          Calcular
        </Button>
      </div>

      {(!ingresoBase || ingresoBase <= 0) && (
        <p className="mt-3 text-xs text-amber-600 flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" /> Sin ingreso estimado de PyP no se puede calcular el monto.
        </p>
      )}

      {res && (
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400">Ingreso base</p>
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100" data-testid="motor-calc-ingreso">{fmtMoney(res.base)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400">Cuota máx.</p>
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100" data-testid="motor-calc-cuota">{fmtMoney(res.cuota)}</p>
          </div>
          <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 p-3">
            <p className="text-[11px] uppercase tracking-wider text-teal-600">Monto máx. a prestar</p>
            <p className="text-lg font-bold text-teal-700 dark:text-teal-300" data-testid="motor-calc-monto">{fmtMoney(res.monto)}</p>
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-400">
        Cuota = Ingreso × RCI. Monto = valor presente de la cuota a {plazo} meses con TNA {tna}%.
      </p>
    </Card>
  );
};

const FUENTES = [
  { key: 'bcra', label: 'BCRA' },
  { key: 'pyp', label: 'PyP' },
  { key: 'nosis', label: 'Nosis' },
  { key: 'afip', label: 'AFIP' },
];

const MotorEvaluar = () => {
  const { isAdmin } = usePermisos();
  const admin = isAdmin();

  const [cuit, setCuit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [refrescar, setRefrescar] = useState(false);
  const [sources, setSources] = useState({ bcra: true, pyp: true, nosis: true, afip: true });
  const [calcCfg, setCalcCfg] = useState({ rci: 30, tna: 120, plazo: 12 });
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    api.get('/motor/calc-config').then((r) => r?.data && setCalcCfg(r.data)).catch(() => {});
  }, []);

  const soloDigitos = (v) => (v || '').replace(/[^0-9]/g, '');
  const toggleSource = (k) => setSources((p) => ({ ...p, [k]: !p[k] }));

  const evaluar = async () => {
    const c = soloDigitos(cuit);
    if (c.length !== 11) { toast.error('El CUIT debe tener 11 dígitos'); return; }
    const fuentes = FUENTES.map((f) => f.key).filter((k) => sources[k]);
    if (fuentes.length === 0) { toast.error('Elegí al menos una fuente para consultar'); return; }
    setLoading(true); setResult(null); setHist(null);
    try {
      const r = await api.post('/motor/evaluar', { cuit: c, fuentes, refrescar });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al evaluar el CUIT');
    } finally { setLoading(false); }
  };

  const verHistorico = async () => {
    if (!result?.cuit) return;
    setHistLoading(true);
    try {
      const r = await api.get(`/motor/bcra/${result.cuit}/historico`);
      setHist(r.data);
    } catch (e) {
      toast.error('No se pudo obtener el histórico BCRA');
    } finally { setHistLoading(false); }
  };

  const onKey = (e) => { if (e.key === 'Enter') evaluar(); };

  const descargarPDF = async () => {
    const id = result?.decision_id || result?.id;
    if (!id) { toast.error('Volvé a evaluar el CUIT para generar el PDF'); return; }
    setPdfLoading(true);
    try {
      const r = await api.get(`/motor/decisiones/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluacion_${result.cuit}_${String(id).slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo generar el PDF');
    } finally { setPdfLoading(false); }
  };

  const bcra = result?.fuentes?.bcra;
  const pyp = result?.fuentes?.pyp;
  const nosis = result?.fuentes?.nosis;
  const afip = result?.fuentes?.afip;
  const apoc = result?.fuentes?.apoc;
  const aprobado = result?.dictamen === 'Aprobar';
  const ingresoBase = (pyp?.ok && pyp?.ingreso_estimado) || result?.estimacion?.ingreso_base || 0;
  const endeudPct = (nosis?.ok && nosis?.compromiso_mensual && ingresoBase > 0)
    ? `${((nosis.compromiso_mensual / ingresoBase) * 100).toFixed(1).replace('.', ',')}%` : '—';

  // Variables que miran el dictamen (grilla resumen, como el legacy)
  const variablesDictamen = result ? [
    ['Edad', pyp?.ok ? (pyp.edad ?? '—') : '—'],
    ['Fallecido (PyP)', pyp?.ok ? (pyp.fallecido || '—') : '—'],
    ['Mora vigente (PyP)', pyp?.ok ? (pyp.moras_vigentes || '—') : '—'],
    ['Ocupación RD', pyp?.ok ? (pyp.ocupacion_rd || '—') : '—'],
    ['Ingreso PyP', fmtMoney(ingresoBase)],
    ['Antigüedad laboral', pyp?.ok && pyp.antiguedad_meses != null ? `${pyp.antiguedad_meses} meses` : '—'],
    ['Sit. BCRA actual', bcra?.ok ? (bcra.peor_situacion ?? bcra.sitMax ?? '—') : '—'],
    ['Sit. BCRA últ. 6m', pyp?.ok ? (pyp.st_ult_6m_bcra ?? '—') : '—'],
    ['Score Nosis', nosis?.ok ? (nosis.score ?? '—') : '—'],
    ['Juicios Nosis 12m', nosis?.ok ? (nosis.juicios_12m ?? '—') : '—'],
    ['Concursos Nosis 12m', nosis?.ok ? (nosis.concursos ?? '—') : '—'],
    ['Endeudamiento', endeudPct],
  ] : [];

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="motor-evaluar">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-teal-500/10"><Gauge className="h-6 w-6 text-teal-600" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Motor de Decisión</h1>
          <p className="text-sm text-slate-500">Evaluá un CUIT contra BCRA, PyP, Nosis y AFIP con reglas configurables</p>
        </div>
      </div>

      {/* Buscador + fuentes */}
      <Card className="p-4 mb-6 border-slate-200 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Input
            value={cuit}
            onChange={(e) => setCuit(e.target.value)}
            onKeyDown={onKey}
            placeholder="CUIT / CUIL (11 dígitos)"
            className="sm:max-w-xs"
            data-testid="motor-cuit-input"
          />
          <Button onClick={evaluar} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="motor-evaluar-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            {loading ? 'Evaluando…' : 'Evaluar'}
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400">Fuentes a consultar</span>
          {FUENTES.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Checkbox
                checked={sources[f.key]}
                onCheckedChange={() => toggleSource(f.key)}
                data-testid={`motor-fuente-${f.key}`}
              />
              {f.label}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none ml-auto" title="Ignora el caché y vuelve a consultar (consume cupo pago en PyP/Nosis)">
            <Checkbox checked={refrescar} onCheckedChange={(v) => setRefrescar(!!v)} data-testid="motor-refrescar-check" />
            Forzar re-consulta
          </label>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          PyP y Nosis consumen cupo pago; podés destildarlas para una consulta parcial gratuita (BCRA/AFIP).
        </p>
      </Card>

      {loading && (
        <div className="text-center text-slate-500 py-10" data-testid="motor-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Consultando fuentes seleccionadas…
        </div>
      )}

      {result && !loading && (
        <Tabs defaultValue="resumen" className="space-y-4" data-testid="motor-tabs">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="resumen" data-testid="motor-tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="bcra" data-testid="motor-tab-bcra">BCRA</TabsTrigger>
            <TabsTrigger value="pyp" data-testid="motor-tab-pyp">PyP</TabsTrigger>
            <TabsTrigger value="nosis" data-testid="motor-tab-nosis">Nosis</TabsTrigger>
            <TabsTrigger value="afip" data-testid="motor-tab-afip">AFIP</TabsTrigger>
          </TabsList>

          {/* ===================== RESUMEN ===================== */}
          <TabsContent value="resumen" className="space-y-4">
            {/* Alerta de Apócrifo (ARCA · WSAPOC) — advertencia, no cambia el dictamen */}
            {result.apocrifo && (
              <Card className="p-4 border-2 border-rose-500 bg-rose-50 dark:bg-rose-950/30" data-testid="motor-apocrifo-alert">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-7 w-7 text-rose-600 shrink-0" />
                  <div>
                    <p className="text-base font-bold text-rose-700 dark:text-rose-300">CONTRIBUYENTE APÓCRIFO (ARCA · WSAPOC)</p>
                    <p className="text-xs text-rose-600 dark:text-rose-400">Este CUIT figura en la base de contribuyentes/facturas apócrifas de ARCA. Revisá antes de operar.</p>
                  </div>
                </div>
              </Card>
            )}
            {/* Dictamen */}
            <Card
              className={`p-5 border ${aprobado ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`}
              data-testid="motor-dictamen"
            >
              <div className="flex items-center gap-3 mb-2">
                {aprobado
                  ? <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  : <XCircle className="h-8 w-8 text-rose-600" />}
                <div>
                  <p className={`text-2xl font-bold ${aprobado ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="motor-dictamen-text">
                    {aprobado ? 'APROBAR' : 'RECHAZAR'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {result.nombre || '—'} · CUIT {result.cuit}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400">Score Nosis</p>
                  <p className={`text-3xl font-bold leading-none ${tierColor(result.score_tier)}`} data-testid="motor-score-value">
                    {result.score ?? '—'}
                  </p>
                  <p className={`text-xs font-medium ${tierColor(result.score_tier)}`}>{result.score_tier || 'Sin dato'}</p>
                </div>
              </div>

              {result.motivos?.length > 0 && (
                <div className="mt-3" data-testid="motor-motivos">
                  <p className="text-xs font-semibold text-rose-700 mb-1.5">Motivos de rechazo</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.motivos.map((m, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-600 text-white">
                        <AlertTriangle className="h-3 w-3" /> {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {result.warnings?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Advertencias</p>
                  <ul className="space-y-0.5">
                    {result.warnings.map((m, i) => (<li key={i} className="text-sm text-amber-700">• {m}</li>))}
                  </ul>
                </div>
              )}

              <div className="flex items-end justify-between gap-3 mt-3 flex-wrap">
                <p className="text-[11px] text-slate-400">
                  Fuentes: {result.fuentes_consultadas?.join(', ') || '—'} · {result.elapsed_ms} ms
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={descargarPDF}
                  disabled={pdfLoading || (!result.decision_id && !result.id)}
                  className="h-8 border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="motor-descargar-pdf-btn"
                >
                  {pdfLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
                  Descargar PDF
                </Button>
              </div>
            </Card>

            {/* Calculadora */}
            <Calculadora
              key={`calc-${calcCfg.rci}-${calcCfg.tna}-${calcCfg.plazo}-${result.cuit}`}
              ingresoBase={ingresoBase}
              config={calcCfg}
              admin={admin}
            />

            {/* Grilla resumen */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-variables-dictamen">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Variables que miran el dictamen</p>
                <div className="space-y-1.5">
                  {variablesDictamen.map(([k, v], i) => (
                    <div key={i} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                      <span className="text-sm text-slate-500">{k}</span>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200 text-right break-words">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-slate-400">Endeudamiento = compromiso mensual (Nosis) / ingreso estimado (PyP). Solo informativo.</p>
              </Card>

              <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-observaciones">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Observaciones</p>
                <div className="flex flex-wrap gap-1.5">
                  {result.motivos?.length > 0
                    ? result.motivos.map((m, i) => (
                        <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-rose-600 text-white">{m}</span>
                      ))
                    : <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Sin observaciones</span>}
                </div>
                {result.warnings?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {result.warnings.map((m, i) => (
                      <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">{m}</span>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-slate-400">El dictamen surge del reglamento configurable (sección Reglamento).</p>
              </Card>
            </div>
          </TabsContent>

          {/* ===================== BCRA ===================== */}
          <TabsContent value="bcra">
            <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-bcra-card">
              <SourceHeader icon={Landmark} title="BCRA · Central de Deudores" ok={bcra?.ok} cached={bcra?._cache} />
              {!bcra?.ok && (
                <div className="flex items-start gap-2 text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5" /><span>{bcra?.error || 'BCRA no consultado'}</span>
                </div>
              )}
              {bcra?.ok && bcra?.sin_registros && (
                <p className="text-sm text-slate-600 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Sin registros de deuda.</p>
              )}
              {bcra?.ok && !bcra?.sin_registros && (
                <div className="space-y-4">
                  {bcra.denominacion && <p className="text-sm text-slate-500 font-mono">{bcra.denominacion}</p>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400">Período</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{fmtPeriodo(bcra.periodo)}</p>
                    </div>
                    <div className={`rounded-lg border p-3 ${sitColor(bcra.peor_situacion)}`}>
                      <p className="text-[11px] uppercase tracking-wider opacity-80">Sit. máx.</p>
                      <p className="text-2xl font-bold leading-tight">{bcra.peor_situacion || 0}</p>
                      <p className="text-[11px]">{bcra.peor_situacion_label || '—'}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400">Deuda total</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100" data-testid="motor-bcra-deuda">{fmtMoney(bcra.total_deuda)}</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400">Entidades</p>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-100">{bcra.total_entidades || 0}</p>
                    </div>
                  </div>

                  <div className={`rounded-lg border p-2 text-sm flex items-center gap-2 ${bcra.cheques_rechazados_qty > 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-600'}`}>
                    <FileWarning className="h-4 w-4" />
                    {bcra.cheques_rechazados_qty > 0
                      ? `${bcra.cheques_rechazados_qty} cheque(s) rechazado(s) · ${fmtMoney(bcra.cheques_rechazados_monto)}`
                      : 'Sin cheques rechazados'}
                  </div>

                  {bcra.entidades?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-slate-500">
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <th className="text-left py-2 font-medium">Entidad</th>
                            <th className="text-center py-2 font-medium">Situación</th>
                            <th className="text-right py-2 font-medium">Deuda</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bcra.entidades.map((e, i) => (
                            <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                              <td className="py-2 text-slate-700 dark:text-slate-200">{e.entidad || '—'}</td>
                              <td className="py-2 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs border ${sitColor(e.situacion)}`}>
                                  {e.situacion} {e.situacion_label ? `· ${e.situacion_label}` : ''}
                                </span>
                              </td>
                              <td className="py-2 text-right font-mono text-slate-700 dark:text-slate-200">{fmtMoney(e.monto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Histórico 24 meses */}
              <div className="mt-4">
                <Button variant="outline" size="sm" onClick={verHistorico} disabled={histLoading || !bcra?.ok} data-testid="motor-historico-btn">
                  {histLoading ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <LineChart className="h-3.5 w-3.5 mr-2" />}
                  Ver evolución 24 meses
                </Button>
                {hist && (
                  <div className="mt-4" data-testid="motor-historico">
                    {!hist.ok && <p className="text-sm text-rose-600">No disponible</p>}
                    {hist.ok && (hist.periodos?.length || 0) === 0 && <p className="text-sm text-slate-500">Sin historial.</p>}
                    {hist.ok && hist.periodos?.length > 0 && (
                      <>
                        <div className="flex items-end gap-1 h-24 border-b border-slate-200 dark:border-slate-700 pb-0">
                          {[...hist.periodos].reverse().map((p, i) => {
                            const peor = Math.max(0, ...p.entidades.map((e) => e.situacion || 0));
                            const h = Math.max(8, (peor / 6) * 100);
                            return (
                              <div key={i} className="flex-1 group relative flex flex-col justify-end items-center" title={`${p.periodo}: situación ${peor}`}>
                                <div className={`w-full rounded-t ${peor ? sitBar(peor) : 'bg-slate-200'}`} style={{ height: `${h}%` }} />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                          <span>{[...hist.periodos].reverse()[0]?.periodo}</span>
                          <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> reciente →</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* ===================== PyP ===================== */}
          <TabsContent value="pyp">
            <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-pyp-card">
              <SourceHeader icon={User2} title="PyP · Datos de persona" ok={pyp?.ok && !pyp?.sin_datos} cached={pyp?._cache} />
              {!pyp?.ok && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <ShieldAlert className="h-4 w-4 mt-0.5" /><span>{pyp?.error || 'PyP no consultado'}</span>
                </div>
              )}
              {pyp?.ok && pyp?.sin_datos && <p className="text-sm text-slate-500">Sin datos para este DNI en PyP.</p>}
              {pyp?.ok && !pyp?.sin_datos && (
                <div className="space-y-4">
                  {/* Encabezado persona */}
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-teal-500/10 border border-teal-500/40 flex items-center justify-center font-semibold text-teal-600">
                      {initials(pyp.apenom || `${pyp.nombre || ''} ${pyp.apellido || ''}`)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-slate-100" data-testid="motor-pyp-nombre">{pyp.apenom || `${pyp.nombre || ''} ${pyp.apellido || ''}`.trim() || '—'}</p>
                      <p className="text-xs text-slate-500">{pyp.ocupacion || '—'} · {pyp.edad ?? '—'} años · {pyp.nacionalidad || '—'}</p>
                    </div>
                    {pyp.score_pyp != null && (
                      <div className="ml-auto text-right">
                        <p className="text-[11px] uppercase tracking-wider text-slate-400">Score PyP</p>
                        <p className="text-2xl font-bold text-slate-700 dark:text-slate-200">{pyp.score_pyp}</p>
                      </div>
                    )}
                  </div>

                  <NosisGroup label="Identificación y domicilio">
                    <KV k="CUIL" v={pyp.cuil} />
                    <KV k="DNI" v={pyp.dni} />
                    <KV k="Sexo" v={pyp.sexo} />
                    <KV k="Fecha nac." v={pyp.fecha_nac} />
                    <KV k="Fallecido" v={pyp.fallecido} />
                    <KV k="Domicilio" v={`${pyp.domicilio?.calle || ''} ${pyp.domicilio?.altura || ''}`.trim() || '—'} />
                    <KV k="Localidad" v={pyp.domicilio?.localidad} />
                    <KV k="Provincia" v={pyp.domicilio?.provincia} />
                    <KV k="CP" v={pyp.domicilio?.cp} />
                  </NosisGroup>

                  <NosisGroup label="Situación fiscal / laboral">
                    <KV k="Ocupación" v={pyp.ocupacion} />
                    <KV k="Rel. dependencia" v={pyp.ocupacion_rd} />
                    <KV k="Empleador" v={pyp.empleador} />
                    <KV k="Antigüedad" v={pyp.antiguedad_meses != null ? `${pyp.antiguedad_meses} meses` : '—'} />
                    <KV k="Período alta últ. trabajo" v={fmtPeriodo(pyp.periodo_ingreso)} />
                    <KV k="Ingreso estimado" v={fmtMoney(pyp.ingreso_estimado)} />
                  </NosisGroup>

                  <NosisGroup label="BCRA vía PyP">
                    <KV k="Entidad vigente" v={pyp.entidad_bcra_vig} />
                    <KV k="Sit. máx. vigente" v={pyp.situacion_max_vig} />
                    <KV k="Sit. últ. 6m" v={pyp.st_ult_6m_bcra} />
                    <KV k="Moras vigentes" v={`${pyp.moras_vigentes || '—'}${pyp.cant_mora_vigente ? ` (${pyp.cant_mora_vigente})` : ''}`} />
                    <KV k="Cheques rechazados" v={pyp.cheques_rechazados} />
                    <KV k="Tiene demanda" v={pyp.tiene_demanda} />
                  </NosisGroup>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ===================== Nosis ===================== */}
          <TabsContent value="nosis">
            <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-nosis-card">
              <SourceHeader icon={Gauge} title="Nosis · Buró de crédito" ok={nosis?.ok} cached={nosis?._cache} />
              {!nosis?.ok && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <ShieldAlert className="h-4 w-4 mt-0.5" /><span>{nosis?.error || 'Nosis no consultado'}</span>
                </div>
              )}
              {nosis?.ok && (
                <NosisDetail nosis={nosis} scoreTier={result.score_tier} ingresoEstimado={ingresoBase} />
              )}
            </Card>
          </TabsContent>

          {/* ===================== AFIP ===================== */}
          <TabsContent value="afip">
            <Card className="p-5 border-slate-200 dark:border-slate-800" data-testid="motor-afip-card">
              <SourceHeader icon={Building2} title="AFIP · Padrón ARCA" ok={afip?.ok} cached={afip?._cache} />
              {!afip?.ok && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <ShieldAlert className="h-4 w-4 mt-0.5" /><span>{afip?.error || 'AFIP no consultado'}</span>
                </div>
              )}
              {afip?.ok && (
                <div className="space-y-3 text-sm">
                  <Field label="Nombre / Razón social" value={afip.razon_social || `${afip.nombre || ''} ${afip.apellido || ''}`.trim()} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Tipo de persona" value={afip.tipo_persona} />
                    <Field label="Estado clave" value={afip.estado_clave} />
                  </div>
                  {afip.actividad_principal && (
                    <Field label="Actividad principal" value={afip.actividad_principal.descripcion || afip.actividad_principal.codigo} />
                  )}
                  {afip.domicilio && (
                    <Field label="Domicilio" value={typeof afip.domicilio === 'string'
                      ? afip.domicilio
                      : (afip.domicilio.direccion_completa
                         || [`${afip.domicilio.calle || ''} ${afip.domicilio.nro || ''}`.trim(), afip.domicilio.localidad, afip.domicilio.provincia].filter(Boolean).join(', ')
                         || afip.domicilio.direccion || '—')} />
                  )}
                  {afip.monotributo && (
                    <Field label="Monotributo" value={typeof afip.monotributo === 'string'
                      ? afip.monotributo
                      : [afip.monotributo.categoria, afip.monotributo.actividad].filter(Boolean).join(' · ')} />
                  )}
                  {Array.isArray(afip.impuestos) && afip.impuestos.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Impuestos</p>
                      <div className="flex flex-wrap gap-1" data-testid="motor-afip-impuestos">
                        {afip.impuestos.map((im, i) => (
                          <span key={i} className="bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 text-[11px]">{im.descripcion || im.id}{im.estado ? ` · ${im.estado}` : ''}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {Array.isArray(afip.domicilios) && afip.domicilios.length > 1 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Otros domicilios (A13)</p>
                      <ul className="space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
                        {afip.domicilios.map((d, i) => (
                          <li key={i}>{d.tipo ? `[${d.tipo}] ` : ''}{[d.direccion, d.localidad, d.provincia].filter(Boolean).join(', ')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {afip.padron && <p className="text-[10px] text-slate-400">Fuente: Padrón {String(afip.padron).toUpperCase()}</p>}
                </div>
              )}
              {/* Apócrifos (WSAPOC) */}
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                {apoc?.ok ? (
                  apoc.es_apocrifo ? (
                    <div className="flex items-center gap-2 text-sm font-semibold text-rose-700" data-testid="motor-afip-apoc">
                      <ShieldAlert className="h-4 w-4" /> Apócrifos (WSAPOC): FIGURA como apócrifo
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="motor-afip-apoc">
                      <CheckCircle2 className="h-4 w-4" /> Apócrifos (WSAPOC): sin observaciones
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="motor-afip-apoc">
                    <ShieldAlert className="h-3.5 w-3.5" /> Apócrifos (WSAPOC): {apoc?.error || 'no consultado'}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {!result && !loading && (
        <div className="text-center text-slate-400 py-16" data-testid="motor-empty">
          <Gauge className="h-10 w-10 mx-auto mb-3 opacity-40" />
          Ingresá un CUIT, elegí las fuentes y presioná «Evaluar» para obtener el dictamen del motor.
        </div>
      )}
    </div>
  );
};

export default MotorEvaluar;
